/**
 * DAFNE Diary-Based Pattern Analysis
 *
 * This module implements the core DAFNE methodology for insulin adjustment decisions.
 * It analyzes blood glucose readings from the diary (not CGM) to identify patterns
 * that require ratio or long-acting insulin changes.
 *
 * KEY DAFNE PRINCIPLES:
 * =====================
 *
 * 1. PRE-MEAL GL TELLS YOU ABOUT THE PREVIOUS PERIOD'S INSULIN
 *    - Pre-breakfast (fasting) → Long-acting insulin adequacy
 *    - Pre-lunch → Breakfast quick-acting adequacy
 *    - Pre-evening meal → Lunch quick-acting adequacy
 *    - Bedtime → Evening meal quick-acting adequacy
 *
 * 2. TWO-TO-THREE-DAY RULE
 *    A pattern must occur on 2-3+ days (not necessarily consecutive)
 *    within the analysis window before recommending changes.
 *    This prevents reacting to one-off readings.
 *
 * 3. SAFETY GATE - EXCLUDE EXPLAINED READINGS
 *    Do NOT adjust if caused by (OzDAFNE Safety Gate):
 *    - Exercise (medium/vigorous activity)
 *    - Alcohol (can lower glucose later, overnight risk)
 *    - Illness / Sick days
 *    - Missed insulin
 *    - Carb miscount / estimation
 *    - Hypo over-treatment
 *
 * 4. DIRECTION OF ADJUSTMENT
 *    - Consistently HIGH pre-meal GL → Need MORE insulin at previous period
 *    - Consistently LOW pre-meal GL → Need LESS insulin at previous period
 *
 * THRESHOLDS (OZDAFNE):
 * ====================
 * - Target range: 4.0 - 7.0 mmol/L (before meals)
 * - Hypo: < 4.0 mmol/L
 * - Above target: > 7.0 mmol/L
 * - Significantly high: > 10.0 mmol/L
 *
 * ADJUSTMENT AMOUNTS (OZDAFNE GUIDANCE):
 * =====================================
 * - Ratio changes: Adjust by 0.5u per CP (e.g., 3u/CP → 3.5u/CP)
 * - Long-acting changes: Adjust by 1-2 units (or ~10-20%)
 * - Re-assess after 2-3 days of the new dose
 * - Avoid adjusting multiple insulins at once
 *
 * ADVANCED PATTERNS IMPLEMENTED:
 * ==============================
 * - Dawn phenomenon detection (3am in-range but breakfast high)
 * - Whole-day-high = long-acting issue (3+ periods high suggests long-acting not ratios)
 *
 * TODO: FUTURE ENHANCEMENTS
 * =========================
 * - Exercise adjustment guidance (proactive reduction suggestions)
 * - Post-alcohol overnight warning
 * - Personalisation layer: individual insulin sensitivity, slow carb behavior
 */

import type { LogEntry, PeriodTag, UserSettings } from "../types";
import { PERIOD_TAG_INFO } from "../types";
import { BG_THRESHOLDS } from "../config/dafne/thresholds";
import { format, subDays, startOfDay } from "date-fns";

// Use GL terminology per app standards, but reference the BG_THRESHOLDS config
const GL_THRESHOLDS = BG_THRESHOLDS;

// ===========================================================================
// Types
// ===========================================================================

/**
 * DAFNE pattern severity - indicates urgency of action
 */
export type DafneSeverity = "action" | "monitor" | "good";

/**
 * Type of DAFNE pattern detected
 */
export type DafnePatternType =
  | "basal-high" // Fasting BG consistently high
  | "basal-low" // Fasting BG consistently low
  | "ratio-high" // Pre-meal BG high (need more QA at previous meal)
  | "ratio-low" // Pre-meal BG low (need less QA at previous meal)
  | "correction-ineffective" // Corrections not working
  | "hypo-pattern" // Recurring hypos at specific time
  | "good-control"; // Pattern shows good control

/**
 * A single reading for pattern analysis
 */
export interface DiaryReading {
  date: Date;
  dateKey: string; // yyyy-MM-dd for grouping
  period: PeriodTag;
  bg: number;
  hasCarbs: boolean;
  hasQA: boolean;
  hasBI: boolean;
  isExplained: boolean; // Sick, unusual exercise, etc.
  explanation?: string;
  entry: LogEntry;
}

/**
 * Aggregated data for a specific period
 */
export interface PeriodSummary {
  period: PeriodTag;
  readings: DiaryReading[];
  validReadings: DiaryReading[]; // Excluding explained
  avgBg: number;
  daysHigh: number;
  daysLow: number;
  daysInRange: number;
  totalDays: number;
  highDates: string[];
  lowDates: string[];
}

/**
 * A DAFNE pattern requiring attention
 */
export interface DafnePattern {
  type: DafnePatternType;
  severity: DafneSeverity;
  title: string;
  detail: string;
  recommendation: string;
  affectedPeriod: PeriodTag;
  causePeriod?: PeriodTag; // The period whose insulin needs adjustment
  data: {
    daysAffected: number;
    totalDays: number;
    avgBg: number;
    readings: DiaryReading[];
    highDates?: string[];
    lowDates?: string[];
  };
}

/**
 * Complete DAFNE analysis result
 */
export interface DafneAnalysisResult {
  patterns: DafnePattern[];
  periodSummaries: PeriodSummary[];
  analysisWindow: {
    start: Date;
    end: Date;
    daysAnalyzed: number;
    daysWithData: number;
  };
  overallAssessment: string;
}

// ===========================================================================
// Constants
// ===========================================================================

/**
 * Maps each period to the period whose insulin it reflects
 * Pre-meal BG tells you about the PREVIOUS period's quick-acting
 * Fasting BG tells you about basal insulin
 */
const PERIOD_TO_CAUSE: Partial<
  Record<PeriodTag, { cause: PeriodTag | "basal"; type: "ratio" | "basal" }>
> = {
  B: { cause: "basal", type: "basal" }, // Fasting reflects basal
  L: { cause: "B", type: "ratio" }, // Pre-lunch reflects breakfast QA
  EM: { cause: "L", type: "ratio" }, // Pre-EM reflects lunch QA
  BT: { cause: "EM", type: "ratio" }, // Bedtime reflects dinner QA
  "3AM": { cause: "basal", type: "basal" }, // 3AM reflects overnight basal
  // after-* periods are not used for pattern analysis
};

/**
 * Period display names for recommendations
 */
const PERIOD_NAMES: Partial<Record<PeriodTag, string>> = {
  B: "breakfast",
  L: "lunch",
  EM: "evening meal",
  BT: "bedtime",
  "3AM": "3AM",
  // after-* periods not used for pattern naming
};

/**
 * Minimum days needed to identify a pattern
 * OzDAFNE guidance: 2-3 days
 * Using 2 to catch patterns sooner (especially hypos for safety)
 */
const MIN_PATTERN_DAYS = 2;

/**
 * Minimum days with any data before analysis is meaningful
 */
const MIN_DATA_DAYS = 3;

/**
 * Default analysis window in days
 */
const DEFAULT_ANALYSIS_DAYS = 7;

// ===========================================================================
// Helper Functions
// ===========================================================================

/**
 * Get timestamp as number handling Date, number, and string
 */
function getTimestamp(ts: Date | number | string): number {
  if (typeof ts === "number") return ts;
  if (typeof ts === "string") return new Date(ts).getTime();
  return ts.getTime();
}

/**
 * Determine if a reading should be excluded from pattern analysis
 *
 * OzDAFNE Safety Gate - Do NOT adjust if caused by:
 * - Exercise
 * - Alcohol
 * - Illness
 * - Missed insulin
 * - Carb miscount
 * - Hypo over-treatment
 */
function isExplainedReading(entry: LogEntry): {
  explained: boolean;
  reason?: string;
} {
  // Sick day / Illness
  if (entry.sickDay?.status === "sick") {
    return { explained: true, reason: "Sick day" };
  }

  // Vigorous exercise (medium+ activity affects BG)
  if (
    entry.exercise?.intensity === "vigorous" ||
    entry.exercise?.intensity === "moderate"
  ) {
    return { explained: true, reason: "Exercise activity" };
  }

  // Hypo over-treatment (carbs without insulin when low)
  if (
    entry.carbs &&
    entry.carbs > 0 &&
    !entry.qaBase &&
    entry.bloodGlucose &&
    entry.bloodGlucose < 4.0
  ) {
    return { explained: true, reason: "Hypo treatment" };
  }

  // Check notes for exclusion triggers
  if (entry.notes) {
    const notes = entry.notes.toLowerCase();

    // Alcohol - can lower glucose later, affects overnight
    if (
      notes.includes("alcohol") ||
      notes.includes("drink") ||
      notes.includes("wine") ||
      notes.includes("beer") ||
      notes.includes("cocktail")
    ) {
      return { explained: true, reason: "Alcohol noted" };
    }

    // Missed insulin
    if (
      notes.includes("forgot") ||
      notes.includes("missed") ||
      notes.includes("skipped") ||
      notes.includes("late dose") ||
      notes.includes("no insulin")
    ) {
      return { explained: true, reason: "Missed/late insulin" };
    }

    // Carb miscount / estimation
    if (
      notes.includes("guessed") ||
      notes.includes("estimate") ||
      notes.includes("unsure") ||
      notes.includes("ate out") ||
      notes.includes("restaurant") ||
      notes.includes("party")
    ) {
      return { explained: true, reason: "Carb estimation" };
    }

    // General unusual circumstances
    if (
      notes.includes("unusual") ||
      notes.includes("stress") ||
      notes.includes("travel")
    ) {
      return { explained: true, reason: "Unusual circumstances" };
    }
  }

  return { explained: false };
}

/**
 * Convert log entries to diary readings for analysis
 */
function entriesToDiaryReadings(
  entries: LogEntry[],
  daysBack: number,
): DiaryReading[] {
  const now = new Date();
  const cutoff = startOfDay(subDays(now, daysBack));
  const readings: DiaryReading[] = [];

  for (const entry of entries) {
    // Skip entries without GL reading
    if (!entry.bloodGlucose || entry.bloodGlucose <= 0) continue;

    // Skip deleted entries
    if ((entry as any)._deletedAt) continue;

    const timestamp = new Date(getTimestamp(entry.timestamp));

    // Skip entries outside analysis window
    if (timestamp < cutoff) continue;

    const { explained, reason } = isExplainedReading(entry);

    readings.push({
      date: timestamp,
      dateKey: format(timestamp, "yyyy-MM-dd"),
      period: entry.periodTag,
      bg: entry.bloodGlucose,
      hasCarbs: (entry.carbs ?? 0) > 0,
      hasQA: (entry.qaBase ?? 0) > 0 || (entry.qaCorrection ?? 0) !== 0,
      hasBI: (entry.biBase ?? 0) > 0,
      isExplained: explained,
      explanation: reason,
      entry,
    });
  }

  return readings;
}

/**
 * Group readings by period
 */
function groupByPeriod(
  readings: DiaryReading[],
): Map<PeriodTag, DiaryReading[]> {
  const groups = new Map<PeriodTag, DiaryReading[]>();

  for (const reading of readings) {
    const existing = groups.get(reading.period) || [];
    existing.push(reading);
    groups.set(reading.period, existing);
  }

  return groups;
}

/**
 * Calculate period summary statistics
 */
function calculatePeriodSummary(
  period: PeriodTag,
  readings: DiaryReading[],
): PeriodSummary {
  const validReadings = readings.filter((r) => !r.isExplained);
  const uniqueDates = new Set(validReadings.map((r) => r.dateKey));

  const highReadings = validReadings.filter(
    (r) => r.bg > GL_THRESHOLDS.TARGET_HIGH_DEFAULT,
  );
  const lowReadings = validReadings.filter((r) => r.bg < GL_THRESHOLDS.HYPO);

  // Get unique dates for each category
  const highDates = [...new Set(highReadings.map((r) => r.dateKey))];
  const lowDates = [...new Set(lowReadings.map((r) => r.dateKey))];

  // Count days in range (not high or low)
  const inRangeDays = uniqueDates.size - highDates.length - lowDates.length;

  const avgBg =
    validReadings.length > 0
      ? validReadings.reduce((sum, r) => sum + r.bg, 0) / validReadings.length
      : 0;

  return {
    period,
    readings,
    validReadings,
    avgBg: Math.round(avgBg * 10) / 10,
    daysHigh: highDates.length,
    daysLow: lowDates.length,
    daysInRange: inRangeDays,
    totalDays: uniqueDates.size,
    highDates,
    lowDates,
  };
}

/**
 * Format a list of dates for display
 */
/** Format a list of dates for display (exported for UI use) */
export function formatDateList(dateKeys: string[]): string {
  if (dateKeys.length === 0) return "";
  if (dateKeys.length <= 3) {
    return dateKeys.map((d) => format(new Date(d), "EEE d MMM")).join(", ");
  }
  return `${dateKeys.length} days`;
}

// ===========================================================================
// Pattern Detection
// ===========================================================================

/**
 * Detect high BG pattern at a period
 */
function detectHighPattern(summary: PeriodSummary): DafnePattern | null {
  if (summary.daysHigh < MIN_PATTERN_DAYS) return null;
  if (summary.totalDays < MIN_PATTERN_DAYS) return null;

  const causePeriod = PERIOD_TO_CAUSE[summary.period];
  const periodName = PERIOD_NAMES[summary.period];

  // Skip periods not in our lookup tables (after-* periods)
  if (!causePeriod || !periodName) return null;

  const percentHigh = Math.round((summary.daysHigh / summary.totalDays) * 100);

  // Determine severity
  const severity: DafneSeverity =
    summary.daysHigh >= 5 || percentHigh >= 70 ? "action" : "monitor";

  if (causePeriod.type === "basal") {
    // Fasting/3AM high - basal issue
    const isOvernight = summary.period === "3AM";
    return {
      type: "basal-high",
      severity,
      title: isOvernight
        ? "🌙 Overnight BG Running High"
        : "☀️ Fasting BG Running High",
      detail: `Your ${periodName} BG has been above ${GL_THRESHOLDS.TARGET_HIGH_DEFAULT} mmol/L on ${summary.daysHigh} of the last ${summary.totalDays} days (avg: ${summary.avgBg} mmol/L).`,
      recommendation: isOvernight
        ? `Consider increasing your evening long-acting insulin by 1-2 units. Your basal may not be lasting through the night, or dawn phenomenon may be starting early.`
        : `Consider increasing your long-acting (basal) insulin by 1-2 units. Wait 2-3 days to assess before making further changes.`,
      affectedPeriod: summary.period,
      causePeriod: undefined,
      data: {
        daysAffected: summary.daysHigh,
        totalDays: summary.totalDays,
        avgBg: summary.avgBg,
        readings: summary.validReadings,
        highDates: summary.highDates,
      },
    };
  } else {
    // Pre-meal high - ratio issue at previous meal
    const cause = causePeriod.cause as PeriodTag;
    const causeName = PERIOD_NAMES[cause] || cause;
    const capitalizedPeriod =
      periodName.charAt(0).toUpperCase() + periodName.slice(1);
    return {
      type: "ratio-high",
      severity,
      title: `📈 Pre-${capitalizedPeriod} BG Running High`,
      detail: `Your BG before ${periodName} has been above ${GL_THRESHOLDS.TARGET_HIGH_DEFAULT} mmol/L on ${summary.daysHigh} of the last ${summary.totalDays} days (avg: ${summary.avgBg} mmol/L). This suggests your ${causeName} quick-acting insulin may be insufficient.`,
      recommendation: `Consider increasing your ${causeName} ratio (e.g., from 3u to 3.5u per CP) to give MORE insulin. Also check carb counting accuracy for typical ${causeName} foods.`,
      affectedPeriod: summary.period,
      causePeriod: cause,
      data: {
        daysAffected: summary.daysHigh,
        totalDays: summary.totalDays,
        avgBg: summary.avgBg,
        readings: summary.validReadings,
        highDates: summary.highDates,
      },
    };
  }
}

/**
 * Detect low BG pattern at a period
 */
function detectLowPattern(summary: PeriodSummary): DafnePattern | null {
  if (summary.daysLow < MIN_PATTERN_DAYS) return null;
  if (summary.totalDays < MIN_PATTERN_DAYS) return null;

  const causePeriod = PERIOD_TO_CAUSE[summary.period];
  const periodName = PERIOD_NAMES[summary.period];

  // Skip periods not in our lookup tables (after-* periods)
  if (!causePeriod || !periodName) return null;

  // Low patterns are always action severity for safety
  const severity: DafneSeverity = "action";

  if (causePeriod.type === "basal") {
    // Fasting/3AM low - basal issue
    const isOvernight = summary.period === "3AM";
    return {
      type: "basal-low",
      severity,
      title: isOvernight
        ? "⚠️ Overnight Hypos Detected"
        : "⚠️ Fasting Hypos Detected",
      detail: `Your ${periodName} BG has been below ${GL_THRESHOLDS.HYPO} mmol/L on ${summary.daysLow} of the last ${summary.totalDays} days. This is a safety concern.`,
      recommendation: isOvernight
        ? `REDUCE your evening long-acting insulin by 1-2 units. Overnight hypos can be dangerous. Check your bedtime BG and consider a bedtime snack if below ${GL_THRESHOLDS.TARGET_HIGH_DEFAULT} mmol/L.`
        : `REDUCE your long-acting (basal) insulin by 1-2 units. Recurring fasting hypos indicate your basal insulin is too high.`,
      affectedPeriod: summary.period,
      causePeriod: undefined,
      data: {
        daysAffected: summary.daysLow,
        totalDays: summary.totalDays,
        avgBg: summary.avgBg,
        readings: summary.validReadings,
        lowDates: summary.lowDates,
      },
    };
  } else {
    // Pre-meal low - ratio issue at previous meal
    const cause = causePeriod.cause as PeriodTag;
    const causeName = PERIOD_NAMES[cause] || cause;
    const capitalizedPeriod =
      periodName.charAt(0).toUpperCase() + periodName.slice(1);
    return {
      type: "ratio-low",
      severity,
      title: `⚠️ Pre-${capitalizedPeriod} Hypos Detected`,
      detail: `Your BG before ${periodName} has been below ${GL_THRESHOLDS.HYPO} mmol/L on ${summary.daysLow} of the last ${summary.totalDays} days. This suggests your ${causeName} quick-acting insulin may be too much.`,
      recommendation: `REDUCE your ${causeName} ratio (e.g., from 3u to 2.5u per CP) to give LESS insulin.`,
      affectedPeriod: summary.period,
      causePeriod: cause,
      data: {
        daysAffected: summary.daysLow,
        totalDays: summary.totalDays,
        avgBg: summary.avgBg,
        readings: summary.validReadings,
        lowDates: summary.lowDates,
      },
    };
  }
}

/**
 * Detect good control pattern at a period
 */
function detectGoodPattern(summary: PeriodSummary): DafnePattern | null {
  if (summary.totalDays < MIN_PATTERN_DAYS) return null;

  const percentInRange = Math.round(
    (summary.daysInRange / summary.totalDays) * 100,
  );

  // Good control if >= 80% in range and no concerning lows
  if (percentInRange >= 80 && summary.daysLow === 0) {
    return {
      type: "good-control",
      severity: "good",
      title: `✓ ${PERIOD_TAG_INFO[summary.period].label} Well Controlled`,
      detail: `Your ${PERIOD_NAMES[summary.period]} BG has been in range on ${summary.daysInRange} of ${summary.totalDays} days (${percentInRange}%). Average: ${summary.avgBg} mmol/L.`,
      recommendation: `Keep doing what you're doing! Your current ${summary.period === "B" || summary.period === "3AM" ? "basal" : "ratios"} appear to be working well for this period.`,
      affectedPeriod: summary.period,
      data: {
        daysAffected: summary.daysInRange,
        totalDays: summary.totalDays,
        avgBg: summary.avgBg,
        readings: summary.validReadings,
      },
    };
  }

  return null;
}

/**
 * Detect dawn phenomenon pattern
 * If 3AM is in-range but B (fasting) is high, this suggests dawn phenomenon
 * rather than insufficient basal insulin overnight
 */
function detectDawnPhenomenon(
  threeAmSummary: PeriodSummary,
  breakfastSummary: PeriodSummary,
): DafnePattern | null {
  // Need enough data at both times
  if (
    threeAmSummary.totalDays < MIN_PATTERN_DAYS ||
    breakfastSummary.totalDays < MIN_PATTERN_DAYS
  ) {
    return null;
  }

  // 3AM should be mostly in range (not low, not high)
  const threeAmInRangePercent = Math.round(
    (threeAmSummary.daysInRange / threeAmSummary.totalDays) * 100,
  );

  // B should be running high
  const breakfastHighPercent = Math.round(
    (breakfastSummary.daysHigh / breakfastSummary.totalDays) * 100,
  );

  // Dawn phenomenon: 3AM ≥ 60% in-range AND breakfast ≥ 50% high
  if (
    threeAmInRangePercent >= 60 &&
    breakfastHighPercent >= 50 &&
    breakfastSummary.daysHigh >= MIN_PATTERN_DAYS
  ) {
    return {
      type: "basal-high", // Using existing type, but distinct recommendation
      severity: "monitor",
      title: "🌅 Possible Dawn Phenomenon",
      detail: `Your 3AM readings are mostly in range (${threeAmInRangePercent}%), but fasting BG is high on ${breakfastSummary.daysHigh} of ${breakfastSummary.totalDays} days. This pattern suggests dawn phenomenon (early morning hormone surge) rather than insufficient overnight basal.`,
      recommendation: `Dawn phenomenon causes a natural rise in BG in the early hours. Options: 1) Consider a small quick-acting correction when you wake, OR 2) If using a pump, consider increasing early morning basal rates. Avoid over-increasing evening basal as this risks overnight hypos.`,
      affectedPeriod: "B",
      data: {
        daysAffected: breakfastSummary.daysHigh,
        totalDays: breakfastSummary.totalDays,
        avgBg: breakfastSummary.avgBg,
        readings: breakfastSummary.validReadings,
        highDates: breakfastSummary.highDates,
      },
    };
  }

  return null;
}

/**
 * Detect whole-day-high pattern (suggests basal issue, not ratio issues)
 * If multiple periods across the day show high BG patterns, the underlying
 * issue is likely basal insulin rather than individual meal ratios
 */
function detectWholeDayHighPattern(
  summaries: PeriodSummary[],
): DafnePattern | null {
  // Check how many periods show HIGH pattern (excluding 3AM which is already basal-related)
  const mealPeriods = summaries.filter((s) => s.period !== "3AM");

  const periodsWithHighPattern = mealPeriods.filter((s) => {
    if (s.totalDays < MIN_PATTERN_DAYS) return false;
    return s.daysHigh >= MIN_PATTERN_DAYS;
  });

  // If 3 or more periods show high patterns, likely a basal issue
  if (periodsWithHighPattern.length >= 3) {
    const avgBgAcrossDay =
      Math.round(
        (mealPeriods.reduce((sum, s) => sum + s.avgBg, 0) /
          mealPeriods.length) *
          10,
      ) / 10;

    const affectedPeriodNames = periodsWithHighPattern
      .map((s) => PERIOD_NAMES[s.period] || s.period)
      .join(", ");

    return {
      type: "basal-high",
      severity: "action",
      title: "📊 High BG Across Multiple Periods",
      detail: `Your BG is running high at ${periodsWithHighPattern.length} different times of day (${affectedPeriodNames}). Average across day: ${avgBgAcrossDay} mmol/L. This widespread pattern suggests your basal (long-acting) insulin may be insufficient, rather than individual meal ratios.`,
      recommendation: `Consider increasing your long-acting insulin by 1-2 units. A single basal adjustment is more appropriate than changing multiple meal ratios when highs occur across the whole day. Monitor for 2-3 days before further changes.`,
      affectedPeriod: "B", // Primary period for sorting
      data: {
        daysAffected: periodsWithHighPattern.length,
        totalDays: mealPeriods[0]?.totalDays || 0,
        avgBg: avgBgAcrossDay,
        readings: periodsWithHighPattern.flatMap((s) => s.validReadings),
      },
    };
  }

  return null;
}

// ===========================================================================
// TODO: Additional Pattern Detection (OzDAFNE Extended)
// ===========================================================================
//
// IMPLEMENTED:
// ✓ Dawn phenomenon detection (3AM in-range but B high)
// ✓ Whole-day-high = basal pattern (3+ periods high)
//
// The following patterns are documented in OzDAFNE but not yet implemented:
//
// 1. POST-ALCOHOL OVERNIGHT WARNING
//    If alcohol was noted the night before:
//    - Flag potential for delayed hypo
//    - Recommend overnight check, bedtime snack consideration
//
// 4. EXERCISE ADJUSTMENT GUIDANCE
//    When prolonged/vigorous exercise is logged:
//    - Proactive suggestion to reduce next quick-acting by ~50%
//    - Consider 20% basal reduction for evening dose
//    - Recommend 3am check for delayed hypo
//
// 5. PERSONALISATION (Future)
//    - Individual insulin sensitivity factors
//    - Slow carb absorption patterns (legumes, etc.)
//    - Personal hypo awareness level
//    - Basal peak timing for different insulins

// ===========================================================================
// Main Analysis Function
// ===========================================================================

/**
 * Perform DAFNE diary-based pattern analysis
 *
 * @param entries - All log entries from the database
 * @param daysToAnalyze - Number of days to analyze (default: 7)
 * @param settings - User's insulin settings for context
 * @returns Complete analysis result with patterns and summaries
 */
export function analyzeDafnePatterns(
  entries: LogEntry[],
  daysToAnalyze: number = DEFAULT_ANALYSIS_DAYS,
  _settings?: UserSettings, // Reserved for future use (ratio display in recommendations)
): DafneAnalysisResult {
  const now = new Date();
  const startDate = startOfDay(subDays(now, daysToAnalyze));

  // Convert entries to diary readings
  const diaryReadings = entriesToDiaryReadings(entries, daysToAnalyze);

  // Group by period
  const periodGroups = groupByPeriod(diaryReadings);

  // Calculate summaries for each period
  const periodSummaries: PeriodSummary[] = [];
  const periods: PeriodTag[] = ["B", "L", "EM", "BT", "3AM"];

  for (const period of periods) {
    const readings = periodGroups.get(period) || [];
    periodSummaries.push(calculatePeriodSummary(period, readings));
  }

  // Detect patterns
  const patterns: DafnePattern[] = [];

  // First check for cross-period patterns (these take precedence)
  const threeAmSummary = periodSummaries.find((s) => s.period === "3AM");
  const breakfastSummary = periodSummaries.find((s) => s.period === "B");

  // Check for dawn phenomenon (3AM in-range but B high)
  let hasDawnPhenomenon = false;
  if (threeAmSummary && breakfastSummary) {
    const dawnPattern = detectDawnPhenomenon(threeAmSummary, breakfastSummary);
    if (dawnPattern) {
      patterns.push(dawnPattern);
      hasDawnPhenomenon = true;
    }
  }

  // Check for whole-day-high pattern (suggests basal issue)
  const wholeDayPattern = detectWholeDayHighPattern(periodSummaries);
  const hasWholeDayHigh = wholeDayPattern !== null;
  if (wholeDayPattern) {
    patterns.push(wholeDayPattern);
  }

  // Now check individual period patterns
  for (const summary of periodSummaries) {
    // Skip individual high pattern for B if dawn phenomenon detected
    // (dawn phenomenon gives better guidance than generic basal-high)
    if (summary.period === "B" && hasDawnPhenomenon) {
      // Still check for lows though
      const lowPattern = detectLowPattern(summary);
      if (lowPattern) patterns.push(lowPattern);
      continue;
    }

    // Skip individual ratio-high patterns if whole-day-high detected
    // (basal adjustment is more appropriate than multiple ratio changes)
    if (hasWholeDayHigh && summary.period !== "B" && summary.period !== "3AM") {
      // Still check for lows (safety priority)
      const lowPattern = detectLowPattern(summary);
      if (lowPattern) patterns.push(lowPattern);
      continue;
    }

    // Check for high pattern
    const highPattern = detectHighPattern(summary);
    if (highPattern) patterns.push(highPattern);

    // Check for low pattern
    const lowPattern = detectLowPattern(summary);
    if (lowPattern) patterns.push(lowPattern);

    // Check for good control (only if no problems)
    if (!highPattern && !lowPattern) {
      const goodPattern = detectGoodPattern(summary);
      if (goodPattern) patterns.push(goodPattern);
    }
  }

  // Sort patterns: action first, then monitor, then good
  const severityOrder: Record<DafneSeverity, number> = {
    action: 0,
    monitor: 1,
    good: 2,
  };
  patterns.sort(
    (a, b) => severityOrder[a.severity] - severityOrder[b.severity],
  );

  // Calculate unique days with data
  const daysWithData = new Set(diaryReadings.map((r) => r.dateKey)).size;

  // Generate overall assessment
  const actionPatterns = patterns.filter((p) => p.severity === "action");
  const monitorPatterns = patterns.filter((p) => p.severity === "monitor");
  const goodPatterns = patterns.filter((p) => p.severity === "good");

  let overallAssessment: string;
  if (actionPatterns.length > 0) {
    overallAssessment = `${actionPatterns.length} pattern${actionPatterns.length > 1 ? "s" : ""} requiring attention. Review the recommendations below.`;
  } else if (monitorPatterns.length > 0) {
    overallAssessment = `${monitorPatterns.length} pattern${monitorPatterns.length > 1 ? "s" : ""} to monitor. No immediate action needed, but keep watching.`;
  } else if (goodPatterns.length > 0) {
    overallAssessment = `Great control! ${goodPatterns.length} period${goodPatterns.length > 1 ? "s" : ""} showing good patterns.`;
  } else if (daysWithData < MIN_DATA_DAYS) {
    overallAssessment = `Not enough data yet. Log BG readings for at least ${MIN_DATA_DAYS} days to see patterns.`;
  } else {
    overallAssessment = `Mixed results. No clear patterns detected. Continue logging for better insights.`;
  }

  return {
    patterns,
    periodSummaries,
    analysisWindow: {
      start: startDate,
      end: now,
      daysAnalyzed: daysToAnalyze,
      daysWithData,
    },
    overallAssessment,
  };
}

/**
 * Get a quick summary of DAFNE patterns for dashboard display
 */
export function getDafneQuickSummary(result: DafneAnalysisResult): {
  hasActionItems: boolean;
  actionCount: number;
  monitorCount: number;
  goodCount: number;
  primaryIssue?: string;
} {
  const actionPatterns = result.patterns.filter((p) => p.severity === "action");
  const monitorPatterns = result.patterns.filter(
    (p) => p.severity === "monitor",
  );
  const goodPatterns = result.patterns.filter((p) => p.severity === "good");

  return {
    hasActionItems: actionPatterns.length > 0,
    actionCount: actionPatterns.length,
    monitorCount: monitorPatterns.length,
    goodCount: goodPatterns.length,
    primaryIssue:
      actionPatterns.length > 0 ? actionPatterns[0].title : undefined,
  };
}
